/**
 * Which workspaces a person may enter, and where they land when they arrive at `/`.
 *
 * Operations used to be "always available" (`requiredPermission: null`), so a user whose role
 * held only Sales — the demo BDE role, 23 Oct 2026 — still got the Operations switcher entry,
 * the Operations sidebar and the Operations dashboard as their home page. Operations now needs
 * `operations:access`, like Finance and Sales need theirs.
 *
 * **Admins keep Operations without the permission.** The live ADMIN role has never held
 * `operations:access` (checked 23 Oct 2026); every other live role with users does. Gating the
 * Admins out of the module they run the business from is not what this change is for.
 *
 * The one place these answers live — the switcher, the layout's workspace sync and the `/`
 * landing all read it, so a menu and a redirect can never disagree.
 */
import { useMemo } from 'react'
import { useAuthStore, type User } from '@/stores/auth-store'

export type WorkspaceId = 'operations' | 'finance' | 'sales'

export interface WorkspaceAccess {
  operations: boolean
  finance: boolean
  /**
   * The Sales workspace can be entered — a Sales seat, or an onboarding approver. Onboarding lives
   * at `/sales/onboarding` and is gated on `onboarding-case:view`, which the four approver roles
   * (Finance Officer, Operations Manager, Accounts Manager, MANAGER) hold without `sales:access`
   * (Madhav, 2026-09-11). They get a Sales workspace filtered to what they can open (`salesItems`
   * in Sidebar.tsx): this widens the door, never the seat — every route keeps its own ModuleGate.
   */
  sales: boolean
  /** A Sales seat proper; without it the Sales workspace is the onboarding queue only. */
  salesSeat: boolean
}

/** The Admin designation, or the system Admin role — either way, Operations stays open. */
export function isAdminUser(user: Pick<User, 'role' | 'roleId'> | null | undefined): boolean {
  if (!user) return false
  return user.role?.toUpperCase() === 'ADMIN' || user.roleId === 'role_admin'
}

export function workspaceAccess(
  permissions: readonly string[],
  user: Pick<User, 'role' | 'roleId'> | null | undefined,
): WorkspaceAccess {
  const has = (p: string) => permissions.includes(p)
  const salesSeat = has('sales:access')
  return {
    operations: has('operations:access') || isAdminUser(user),
    finance: has('finance:access'),
    sales: salesSeat || has('onboarding-case:view'),
    salesSeat,
  }
}

/** The workspace a person belongs in when the URL does not say: Operations, else Sales, else Finance. */
export function homeWorkspace(access: WorkspaceAccess): WorkspaceId {
  if (access.operations) return 'operations'
  if (access.sales) return 'sales'
  if (access.finance) return 'finance'
  return 'operations'
}

/**
 * Where `/` sends someone. Operations users keep the Operations dashboard; everyone else goes to
 * the first workspace they may enter — never to a page that would only say "no access".
 * Someone with no workspace at all stays on `/`, which shows them the no-access screen.
 */
export function landingRoute(access: WorkspaceAccess): string {
  switch (homeWorkspace(access)) {
    case 'sales':
      return access.salesSeat ? '/sales/home' : '/sales/onboarding'
    case 'finance':
      return access.finance ? '/finance' : '/'
    default:
      return '/'
  }
}

/** [workspaceAccess] for the signed-in user. While nobody is signed in, every answer is false. */
export function useWorkspaceAccess(): WorkspaceAccess {
  const user = useAuthStore((s) => s.user)
  return useMemo(() => workspaceAccess(user?.permissions ?? [], user), [user])
}
